import React, { useEffect, useState } from "react";
import api, { API, SHARE } from "@/lib/api";
import { waLink } from "@/lib/wa";
import { useLang } from "@/context/LangContext";
import { IMAGES } from "@/constants/images";
import PageHero from "@/components/public/PageHero";
import ProductCard from "@/components/public/ProductCard";
import EmptyState from "@/components/public/EmptyState";
import StockBadge from "@/components/public/StockBadge";
import CTASection from "@/components/public/CTASection";
import { Search, LayoutGrid, Table2, FileDown, FileSpreadsheet, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

const STATUSES = ["ALL", "AVAILABLE", "LIMITED", "ON_REQUEST", "SOLD_OUT"];

export default function AvailableStock() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [view, setView] = useState("grid");
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/products");
        setProducts(res.data);
        const latest = res.data.map((p) => p.updated_at).sort().reverse()[0];
        setLastUpdated(latest);
      } catch (e) {} finally { setLoading(false); }
    })();
  }, []);

  const filtered = products.filter((p) => {
    const matchQ = !q || [p.product_name, p.origin, p.region, p.process, p.variety].join(" ").toLowerCase().includes(q.toLowerCase());
    const matchS = status === "ALL" || p.stock_status === status;
    return matchQ && matchS;
  });

  return (
    <div>
      <PageHero eyebrow="Live Inventory" title="Available Green Coffee Stock" sub={t("stock.sub")} image={IMAGES.sackPile} />
      <section className="mx-auto max-w-[1400px] px-4 py-14 sm:px-8 lg:px-12">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-espresso/40" />
            <input
              data-testid="stock-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search coffee, origin, process…"
              className="w-full rounded-full border border-forest/15 bg-white py-3 pl-10 pr-4 font-body text-sm text-espresso outline-none focus:border-forest/40"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`stock-filter-${s.toLowerCase()}`}
                onClick={() => setStatus(s)}
                className={`rounded-full border px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider transition-colors ${status === s ? "border-forest bg-forest text-offwhite" : "border-forest/15 bg-white text-espresso/70 hover:border-forest/40"}`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
            <div className="ml-1 hidden items-center gap-1 rounded-full border border-forest/15 bg-white p-1 sm:flex">
              <button data-testid="view-grid" onClick={() => setView("grid")} className={`rounded-full p-2 ${view === "grid" ? "bg-forest text-offwhite" : "text-espresso/50"}`}><LayoutGrid className="h-4 w-4" /></button>
              <button data-testid="view-table" onClick={() => setView("table")} className={`rounded-full p-2 ${view === "table" ? "bg-forest text-offwhite" : "text-espresso/50"}`}><Table2 className="h-4 w-4" /></button>
            </div>
          </div>
        </div>

        {products.length > 0 && (
          <div data-testid="price-sheet-bar" className="mb-8 flex flex-col gap-4 rounded-xl border border-forest/10 bg-beige/50 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-forest text-offwhite"><FileSpreadsheet className="h-5 w-5" /></div>
              <div>
                <div className="font-display text-sm font-bold uppercase tracking-wider text-forest-dark">{t("stock.sheetTitle")}</div>
                <p className="mt-1 font-body text-sm text-espresso/70">{t("stock.sheetSub")}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={`${API}/price-sheet.pdf`} data-testid="download-price-sheet-pdf" className="inline-flex items-center gap-2 rounded-full border border-forest bg-forest px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-offwhite transition-colors hover:bg-forest-dark">
                <FileDown className="h-4 w-4" /> {t("stock.downloadPdf")}
              </a>
              <a href={`${API}/price-sheet.xlsx`} data-testid="download-price-sheet-xlsx" className="inline-flex items-center gap-2 rounded-full border border-forest/25 bg-white px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-forest transition-colors hover:bg-forest/5">
                <FileSpreadsheet className="h-4 w-4" /> {t("stock.downloadExcel")}
              </a>
              <a href={waLink("", `${t("share.priceSheetText")} ${SHARE.priceSheet}`)} target="_blank" rel="noreferrer" data-testid="share-price-sheet-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 bg-white px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#128C7E] transition-colors hover:bg-[#25D366]/10">
                <MessageCircle className="h-4 w-4" /> {t("share.whatsapp")}
              </a>
            </div>
          </div>
        )}

        {lastUpdated && (
          <div className="mb-6 font-mono text-[11px] uppercase tracking-wider text-espresso/50">{t("stock.lastUpdated")}: {new Date(lastUpdated).toLocaleDateString()}</div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">{[0,1,2,3,4,5].map(i => <div key={i} className="h-80 animate-pulse rounded-xl bg-forest/5" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState message={products.length === 0 ? t("stock.empty") : "No coffee matches your filters."} testId="stock-empty" />
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-forest/10 bg-white">
            <table className="w-full min-w-[820px] text-left">
              <thead className="border-b border-forest/10 bg-beige/60">
                <tr className="font-mono text-[10px] uppercase tracking-wider text-espresso/60">
                  {["Product", "Origin", "Process", "Grade", "Stock", "Status", ""].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} data-testid={`stock-row-${p.slug || p.id}`} className="border-b border-forest/5 font-body text-sm hover:bg-beige/40">
                    <td className="px-5 py-4 font-semibold text-forest-dark">{p.product_name}</td>
                    <td className="px-5 py-4 text-espresso/75">{p.region || p.origin || "—"}</td>
                    <td className="px-5 py-4 font-mono text-xs text-espresso/75">{p.process || "—"}</td>
                    <td className="px-5 py-4 font-mono text-xs text-espresso/75">{p.grade || "—"}</td>
                    <td className="px-5 py-4 font-mono text-xs text-espresso/75">{p.available_stock > 0 ? `${p.available_stock.toLocaleString()} ${p.stock_unit}` : "—"}</td>
                    <td className="px-5 py-4"><StockBadge status={p.stock_status} /></td>
                    <td className="px-5 py-4 text-right"><Link to={`/product/${p.slug || p.id}`} className="font-mono text-[11px] font-semibold uppercase text-forest hover:text-amber-brand">View →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <CTASection />
    </div>
  );
}
